import {codeScanningAlertHovercardPath, codeScanningAlertPath} from '@github-ui/paths'
import type {IconProps} from '@primer/octicons-react'
import {ShieldCheckIcon, ShieldIcon, ShieldSlashIcon} from '@primer/octicons-react'
import {ActionList, Link} from '@primer/react'
import {clsx} from 'clsx'
import type {FC} from 'react'
import {graphql, useFragment} from 'react-relay'

import type {
  CodeScanningSecurityAlertStatus,
  LinkedSecurityAlert$key,
} from './__generated__/LinkedSecurityAlert.graphql'
import styles from './LinkedSecurityAlert.module.css'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const LinkedSecurityAlertFragment = graphql`
  fragment LinkedSecurityAlert on CodeScanningSecurityAlert {
    number
    title
    status
    repository {
      name
      owner {
        login
      }
    }
  }
`

type LinkedSecurityAlertType = {
  alertKey: LinkedSecurityAlert$key
  issueRepositoryName: string
  itemKey?: string
  isSmall?: boolean
}

export const LinkedSecurityAlertLinkItem: FC<LinkedSecurityAlertType> = props => {
  const {alertKey, isSmall} = props
  const alert = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment LinkedSecurityAlertLinkItem on CodeScanningSecurityAlert {
        ...LinkedSecurityAlertIcon
        number
        status
        repository {
          name
          owner {
            login
          }
        }
      }
    `,
    alertKey,
  )
  if (!alert.repository || !alert.number) {
    return null
  }
  const owner = alert.repository.owner.login
  const repoName = alert.repository.name

  const hoverCard = codeScanningAlertHovercardPath({
    owner,
    repo: repoName,
    alertNumber: alert.number,
  })
  const alertUrl = codeScanningAlertPath({
    owner,
    repo: repoName,
    alertNumber: alert.number,
  })
  return (
    <Link
      href={alertUrl}
      data-hovercard-url={!isSmall ? hoverCard : undefined}
      target="_blank"
      role="link"
      className={clsx(styles.securityAlertLink)}
    >
      <LinkedSecurityAlertIcon alertKey={props.alertKey} />
      {!isSmall ? <LinkedAlertName {...props} /> : <span>{`Alert: #${alert.number}`}</span>}
    </Link>
  )
}

export const LinkedSecurityAlertListItem: FC<LinkedSecurityAlertType> = props => {
  const {alertKey, itemKey} = props
  const alert = useFragment(LinkedSecurityAlertFragment, alertKey)
  if (!alert.repository || !alert.number) {
    return null
  }
  const owner = alert.repository.owner.login
  const repoName = alert.repository.name

  const alertUrl = codeScanningAlertPath({
    owner,
    repo: repoName,
    alertNumber: alert.number,
  })

  return (
    <ActionList.LinkItem href={alertUrl} key={itemKey} target="_blank">
      <ActionList.LeadingVisual>
        <LinkedSecurityAlertIcon alertKey={props.alertKey} />
      </ActionList.LeadingVisual>
      <LinkedAlertName {...props} expandedTitle />
    </ActionList.LinkItem>
  )
}

type AlertStatus = keyof typeof alertIconConfig

const alertIconConfig = {
  FIXED: {Icon: ShieldCheckIcon, className: 'fgColor-done'},
  OPEN: {Icon: ShieldIcon, className: 'fgColor-open'},
  DISMISSED: {Icon: ShieldSlashIcon, className: 'fgColor-closed'},
} as const satisfies Partial<Record<CodeScanningSecurityAlertStatus, {Icon: FC<IconProps>; className: string}>>

export const LinkedSecurityAlertIcon: FC<Pick<LinkedSecurityAlertType, 'alertKey'> & IconProps> = ({
  alertKey,
  ...props
}) => {
  const alert = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment LinkedSecurityAlertIcon on CodeScanningSecurityAlert {
        status
      }
    `,
    alertKey,
  )
  const status = alert.status && alert.status in alertIconConfig ? alert.status : 'OPEN'
  const {Icon, className} = alertIconConfig[status as AlertStatus]
  return <Icon className={className} {...props} />
}

export const LinkedAlertName: FC<LinkedSecurityAlertType & {expandedTitle?: boolean}> = ({alertKey, expandedTitle}) => {
  const alert = useFragment(LinkedSecurityAlertFragment, alertKey)
  // Always show the alert number and only truncate the title
  return (
    <div className={clsx(styles.titleContainer)}>
      <span className={clsx(styles.alertTitle, expandedTitle && styles.listItemTitle)}>{`Alert: ${alert.title}`}</span>
      <span className={styles.alertNumber}>#{alert.number}</span>
    </div>
  )
}
